import React, {useCallback, useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {
    Ban,
    Calculator,
    Camera,
    ChevronRight,
    FileText,
    Globe,
    Menu,
    MessageSquare,
    Phone,
    Shield,
    Sparkles,
    X,
} from 'lucide-react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {ThemeToggle} from './ThemeToggle';
import bannerWhite from '../assets/banner_white.svg';
import bannerDark from '../assets/banner_dark.svg';

interface NavLinkDef {
    label: string;
    href: string;
    sectionId: string | null;
    icon: typeof Shield;
}

const NAV_LINKS: NavLinkDef[] = [
    {label: 'Usługi', href: '/#uslugi', sectionId: 'uslugi', icon: Shield},
    {label: 'Cennik', href: '/#cennik', sectionId: 'cennik', icon: Calculator},
    {label: 'Wykonania', href: '/wykonania', sectionId: null, icon: Camera},
    {label: 'Kalkulator', href: '/kalkulator', sectionId: null, icon: Sparkles},
    {label: 'Blokada reklam', href: '/blokada-reklam', sectionId: null, icon: Ban},
    {label: 'Kontakt', href: '/kontakt', sectionId: null, icon: MessageSquare},
    {label: 'Strony internetowe', href: 'https://onexo.pl/', sectionId: null, icon: Globe},
];

function NavAnchor({
                       href,
                       onClick,
                       children,
                       className,
                       onMouseEnter,
                       onMouseLeave,
                   }: {
    href: string;
    onClick?: () => void;
    children: React.ReactNode;
    className?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}) {
    const location = useLocation();
    const navigate = useNavigate();

    const handleHashClick = (e: React.MouseEvent) => {
        e.preventDefault();
        onClick?.();

        const id = href.slice(2);

        if (location.pathname === '/') {
            document.getElementById(id)?.scrollIntoView({behavior: 'smooth'});
        } else {
            navigate('/', {state: {scrollTo: id}});
        }
    };

    if (href.startsWith('/#')) {
        return (
            <a
                href={href}
                className={className}
                onClick={handleHashClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {children}
            </a>
        );
    }

    if (href.startsWith('http')) {
        return (
            <a
                href={href}
                className={className}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {children}
            </a>
        );
    }

    return (
        <Link
            to={href}
            className={className}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {children}
        </Link>
    );
}

interface NavbarProps {
    theme: string;
    setTheme: (theme: string) => void;
}

export function Navbar({theme, setTheme}: NavbarProps) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [hoveredLink, setHoveredLink] = useState<string | null>(null);
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', onScroll, {passive: true});
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        if (location.pathname !== '/') {
            setActiveSection(null);
            return;
        }

        const ids = NAV_LINKS.map((l) => l.sectionId).filter(Boolean) as string[];

        const computeActive = () => {
            const triggerY = window.innerHeight * 0.35;
            let current: string | null = null;

            for (const id of ids) {
                const el = document.getElementById(id);
                if (!el) continue;
                if (el.getBoundingClientRect().top <= triggerY) {
                    current = id;
                }
            }

            setActiveSection(current);
        };

        let rafId = 0;
        const onScroll = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(computeActive);
        };

        const timer = setTimeout(() => {
            computeActive();
            window.addEventListener('scroll', onScroll, {passive: true});
        }, 150);

        return () => {
            clearTimeout(timer);
            cancelAnimationFrame(rafId);
            window.removeEventListener('scroll', onScroll);
        };
    }, [location.pathname]);

    useEffect(() => {
        const state = location.state as { scrollTo?: string } | null;
        if (state?.scrollTo) {
            const timer = setTimeout(() => {
                document.getElementById(state.scrollTo!)?.scrollIntoView({behavior: 'smooth'});
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [location.state]);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileOpen]);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const isActive = useCallback(
        (link: NavLinkDef) => {
            if (link.sectionId) {
                return location.pathname === '/' && activeSection === link.sectionId;
            }
            return location.pathname === link.href;
        },
        [location.pathname, activeSection],
    );

    const closeMobile = useCallback(() => setMobileOpen(false), []);
    const wycenaActive = location.pathname === '/wycena';

    return (
        <>
            <motion.nav
                initial={{y: -100}}
                animate={{y: 0}}
                transition={{duration: 0.6, ease: [0.22, 1, 0.36, 1]}}
                className={`fixed top-0 left-0 right-0 z-[1100] bg-background transition-colors duration-300 ${
                    scrolled
                        ? 'shadow-[0_4px_30px_rgba(0,0,0,0.08)] border-b border-border/40'
                        : 'border-b border-border/20'
                }`}
            >
                <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-16 md:h-[72px]">
                    <Link to="/" className="flex items-center gap-2.5 group" aria-label="Kamery Mielec strona główna">
                        <img
                            src={theme === 'dark' ? bannerWhite : bannerDark}
                            alt="Kamery Mielec"
                            className="h-8 md:h-10 w-auto object-contain"
                        />
                    </Link>

                    <DesktopNav
                        links={NAV_LINKS}
                        isActive={isActive}
                        hoveredLink={hoveredLink}
                        setHoveredLink={setHoveredLink}
                        closeMobile={closeMobile}
                    />

                    <div className="flex items-center gap-2 sm:gap-2.5">
                        <motion.a
                            href="tel:+48664896662"
                            whileHover={{scale: 1.03}}
                            whileTap={{scale: 0.97}}
                            className="hidden sm:inline-flex lg:hidden items-center gap-1.5 px-4 py-2 rounded-xl text-muted-foreground text-sm font-medium hover:text-foreground hover:bg-muted/50 transition-colors"
                        >
                            <Phone size={14}/> Zadzwoń
                        </motion.a>

                        <Link to="/wycena" className="hidden lg:block relative group">
                            <motion.div
                                whileHover={{scale: 1.04}}
                                whileTap={{scale: 0.97}}
                                className={`relative flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold overflow-hidden transition-shadow duration-300 ${
                                    wycenaActive
                                        ? 'bg-primary text-white shadow-lg shadow-primary/30'
                                        : 'bg-gradient-to-r from-primary to-primary-dark text-white shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30'
                                }`}
                            >
                                <div
                                    className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out bg-gradient-to-r from-transparent via-white/20 to-transparent"/>
                                <Sparkles size={14} className="relative z-10"/>
                                <span className="relative z-10">Bezpłatna wycena</span>
                            </motion.div>
                        </Link>

                        <motion.button
                            whileTap={{scale: 0.9}}
                            className="lg:hidden flex items-center justify-center w-11 h-11 rounded-xl border border-border/50 bg-card/50 backdrop-blur-md hover:bg-muted transition-all"
                            onClick={() => setMobileOpen((v) => !v)}
                            aria-label="Menu"
                        >
                            <AnimatePresence mode="wait" initial={false}>
                                {mobileOpen ? (
                                    <motion.span
                                        key="close"
                                        initial={{rotate: -90, scale: 0.5, opacity: 0}}
                                        animate={{rotate: 0, scale: 1, opacity: 1}}
                                        exit={{rotate: 90, scale: 0.5, opacity: 0}}
                                        transition={{duration: 0.2}}
                                    >
                                        <X size={20}/>
                                    </motion.span>
                                ) : (
                                    <motion.span
                                        key="menu"
                                        initial={{rotate: 90, scale: 0.5, opacity: 0}}
                                        animate={{rotate: 0, scale: 1, opacity: 1}}
                                        exit={{rotate: -90, scale: 0.5, opacity: 0}}
                                        transition={{duration: 0.2}}
                                    >
                                        <Menu size={20}/>
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </motion.button>

                        <ThemeToggle theme={theme} setTheme={setTheme}/>
                    </div>
                </div>
            </motion.nav>

            <AnimatePresence>
                {mobileOpen && (
                    <MobileMenu
                        isActive={isActive}
                        onClose={closeMobile}
                        theme={theme}
                    />
                )}
            </AnimatePresence>
        </>
    );
}

function DesktopNav({
                        links,
                        isActive,
                        hoveredLink,
                        setHoveredLink,
                        closeMobile,
                    }: {
    links: readonly NavLinkDef[];
    isActive: (l: NavLinkDef) => boolean;
    hoveredLink: string | null;
    setHoveredLink: (v: string | null) => void;
    closeMobile: () => void;
}) {
    return (
        <div className="hidden lg:flex flex-1 items-center justify-center">
            <div
                className="relative flex items-center gap-0.5 rounded-2xl bg-card/70 backdrop-blur-xl border border-border/40 px-1.5 py-1.5 shadow-lg shadow-black/[0.03] dark:shadow-black/20">
                {links.map((link) => {
                    const Icon = link.icon;
                    const active = isActive(link);
                    const hovered = hoveredLink === link.href;
                    // Płynne przesuwanie wskaźnika (layoutId) tylko między sekcjami
                    // na stronie głównej. Przy aktywnej podstronie (route) wskaźnik
                    // pojawia się w miejscu, żeby nie "przeskakiwał" przez navbar.
                    const sectionActive = active && link.sectionId != null;

                    return (
                        <NavAnchor
                            key={link.href}
                            href={link.href}
                            onClick={closeMobile}
                            onMouseEnter={() => setHoveredLink(link.href)}
                            onMouseLeave={() => setHoveredLink(null)}
                            className="relative text-sm font-medium px-3 py-2 rounded-xl cursor-pointer select-none whitespace-nowrap"
                        >
                            {hovered && !active && (
                                <motion.div
                                    layoutId="nav-hover"
                                    className="absolute inset-0 rounded-xl bg-muted/70"
                                    transition={{type: 'spring', stiffness: 400, damping: 30}}
                                />
                            )}

                            {active && (
                                sectionActive ? (
                                    <motion.div
                                        layoutId="nav-active"
                                        className="absolute inset-0 rounded-xl bg-primary"
                                        style={{boxShadow: '0 0 20px rgba(94,40,255,0.3), 0 4px 12px rgba(94,40,255,0.2)'}}
                                        transition={{type: 'spring', stiffness: 350, damping: 30}}
                                    />
                                ) : (
                                    <motion.div
                                        initial={{opacity: 0}}
                                        animate={{opacity: 1}}
                                        className="absolute inset-0 rounded-xl bg-primary"
                                        style={{boxShadow: '0 0 20px rgba(94,40,255,0.3), 0 4px 12px rgba(94,40,255,0.2)'}}
                                    />
                                )
                            )}

                            <span
                                className={`relative z-10 flex items-center gap-1.5 transition-colors duration-200 ${
                                    active
                                        ? 'text-white font-semibold'
                                        : hovered
                                            ? 'text-foreground'
                                            : 'text-muted-foreground'
                                }`}
                            >
								<Icon size={15} className={active ? 'scale-110' : 'scale-90 opacity-60'}/>
                                {link.label}
							</span>
                        </NavAnchor>
                    );
                })}
            </div>
        </div>
    );
}

const MOBILE_LINKS: readonly NavLinkDef[] = [
    ...NAV_LINKS,
    {label: 'Wycena', href: '/wycena', sectionId: null, icon: FileText} as NavLinkDef,
];

function MobileMenu({
                        isActive,
                        onClose,
                        theme,
                    }: {
    isActive: (l: NavLinkDef) => boolean;
    onClose: () => void;
    theme: string;
}) {
    return (
        <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.25}}
            // z-index below the navbar (z-[1100]) so the top bar — logo, close (X)
            // button and theme toggle — stays on top and clickable while open.
            className="fixed inset-0 z-[1050] lg:hidden"
        >
            <motion.div
                className="absolute inset-0 bg-background"
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                onClick={onClose}
            />

            <motion.div
                initial={{y: 24, opacity: 0}}
                animate={{y: 0, opacity: 1}}
                exit={{y: 12, opacity: 0}}
                transition={{duration: 0.35, ease: [0.22, 1, 0.36, 1]}}
                // Full height, scrollable when content is taller than the viewport
                // (small / landscape screens). Top padding clears the fixed navbar.
                className="relative h-full overflow-y-auto overscroll-contain flex flex-col px-6 pt-24 pb-10"
            >
                <div className="w-full max-w-sm mx-auto my-auto flex flex-col gap-2.5">
                    {MOBILE_LINKS.map((link, i) => {
                        const Icon = link.icon;
                        const active = isActive(link);
                        const isWycena = link.href === '/wycena';

                        return (
                            <motion.div
                                key={link.href}
                                initial={{opacity: 0, y: 10}}
                                animate={{opacity: 1, y: 0}}
                                transition={{delay: 0.05 + i * 0.06, duration: 0.35, ease: [0.22, 1, 0.36, 1]}}
                            >
                                <NavAnchor
                                    href={link.href}
                                    onClick={onClose}
                                    className={`group flex items-center gap-4 text-lg font-semibold py-4 px-5 rounded-2xl transition-all duration-200 active:scale-[0.98] ${
                                        active
                                            ? 'text-primary bg-primary/10 border border-primary/20 shadow-sm shadow-primary/10'
                                            : isWycena
                                                ? 'bg-gradient-to-r from-primary to-primary-dark text-white shadow-md shadow-primary/20'
                                                : 'text-foreground hover:bg-muted/60 border border-transparent'
                                    }`}
                                >
                                    <div
                                        className={`flex items-center justify-center w-11 h-11 rounded-xl transition-colors ${
                                            active ? 'bg-primary/15' : isWycena ? 'bg-white/15' : 'bg-muted/60'
                                        }`}
                                    >
                                        <Icon
                                            size={20}
                                            className={active ? 'text-primary' : isWycena ? 'text-white' : 'text-muted-foreground'}
                                        />
                                    </div>

                                    {link.label}

                                    {isWycena && !active && (
                                        <motion.div
                                            animate={{x: [0, 4, 0]}}
                                            transition={{repeat: Infinity, duration: 1.5, ease: 'easeInOut'}}
                                            className="ml-auto"
                                        >
                                            <ChevronRight size={18}/>
                                        </motion.div>
                                    )}
                                    {active && <div className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse"/>}
                                </NavAnchor>
                            </motion.div>
                        );
                    })}

                    <motion.a
                        href="tel:+48664896662"
                        initial={{opacity: 0, y: 10}}
                        animate={{opacity: 1, y: 0}}
                        transition={{delay: 0.35, duration: 0.35, ease: [0.22, 1, 0.36, 1]}}
                        className="flex items-center justify-center gap-2.5 mt-4 py-4 px-5 rounded-2xl bg-muted/80 text-foreground font-semibold text-lg hover:bg-muted transition-colors"
                    >
                        <Phone size={20} className="text-primary"/>
                        +48 664 896 662
                    </motion.a>
                </div>

                <motion.div
                    initial={{opacity: 0, y: 10}}
                    animate={{opacity: 1, y: 0}}
                    transition={{delay: 0.5, duration: 0.4}}
                    className="mt-10 flex items-center justify-center gap-2 text-sm text-muted-foreground"
                >
                    <img
                        src={theme === 'dark' ? bannerWhite : bannerDark}
                        alt="Kamery Mielec"
                        className="h-7 w-auto object-contain"
                    />
                </motion.div>
            </motion.div>
        </motion.div>
    );
}
